import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { CommunityId, daysAgo, formatCurrency } from "@sp-crm/core";
import { Icon } from "components/icon";
import { ContentFull, ContentSection2Up, ContentSectionColumn } from "components/layout";
import { NgGrid } from "components/shared/grid";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import {
    EvergreenCommunityListingAvailability,
    FindOrCreateCommunityInsightsMutation,
    useFindOrCreateCommunityInsightsMutation,
} from "generated/graphql";
import React, { useCallback, useEffect } from "react";
import { useProductName } from "store/selectors/branding";
import { useCanRequestCommunityAvailability, useFeature } from "store/selectors/hooks";
import { PhoneSingleNumber } from "util/phone-display";
import { handleEvent } from "util/user-events";
import { CommunityInsightsReviews } from "./community-insights-reviews";
import { CommunityRequestAvailabilityPanel } from "./community-request-availability-panel";

interface CommunityDetailsProps {
    listing: FindOrCreateCommunityInsightsMutation["findOrCreateCommunityInsights"];
}

const CommunityDetails = ({ listing }: CommunityDetailsProps) => {
    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                    <div className="w-7">
                        <Icon name="57_Community" />
                    </div>
                    <span className="text-lg text-gray-700">Community Details</span>
                </div>
            </div>
            <p className="mb-2">
                <strong>Name:</strong> {listing.name}
            </p>
            <p className="mb-2">
                <strong>Address:</strong> {listing.address} {listing.city},{" "}
                {listing.state} {listing.zip}
            </p>
            {listing.website ? (
                <p className="mb-2">
                    <strong>Website:</strong>{" "}
                    <a href={listing.website} target="_blank" rel="noreferrer">
                        {listing.website}
                    </a>
                </p>
            ) : null}
            {listing.phoneNumbers?.map(phoneNumber => (
                <div
                    className="mb-2 flex items-center space-x-1"
                    key={phoneNumber.number}>
                    <strong>{phoneNumber.label || "Phone"}:</strong>{" "}
                    <PhoneSingleNumber phoneNumber={phoneNumber.number} />
                </div>
            ))}
        </>
    );
};

interface Props {
    listing: FindOrCreateCommunityInsightsMutation["findOrCreateCommunityInsights"];
    communityId: CommunityId;
}

const CommunityAvailability = (props: Props) => {
    const canRequestAvailability = useCanRequestCommunityAvailability();
    const { listing, communityId } = props;
    const [isRequestDialogOpen, setIsRequestDialogOpen] = React.useState(false);
    const [hasRequestedAvailability, setHasRequestedAvailability] = React.useState(false);
    const requestAvailability = useCallback(() => {
        handleEvent("request-community-availability", {
            communityId,
            listingId: listing.id,
        });
        setIsRequestDialogOpen(true);
    }, [communityId, listing.id]);
    const closeRequestDialog = useCallback(() => {
        setIsRequestDialogOpen(false);
    }, []);
    const onRequestSuccess = useCallback(() => {
        setHasRequestedAvailability(true);
        closeRequestDialog();
        handleEvent("request-community-availability-success", {
            communityId,
            listingId: listing.id,
        });
    }, [closeRequestDialog, communityId, listing.id]);
    return (
        <>
            {isRequestDialogOpen ? (
                <CommunityRequestAvailabilityPanel
                    communityId={communityId}
                    onDismiss={closeRequestDialog}
                    onSuccess={onRequestSuccess}
                />
            ) : null}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                    <div className="w-7">
                        <Icon name="42_ClientAndCommunity" />
                    </div>
                    <span className="text-lg text-gray-700">Availability</span>
                </div>
            </div>
            {listing.availability?.status ===
            EvergreenCommunityListingAvailability.Unknown ? (
                <div className="space-y-4 mt-2">
                    <div className="text-sm text-gray-500">
                        {listing.name} has not yet reported any availability to Community
                        Insights.
                    </div>
                    {canRequestAvailability ? (
                        <div>
                            <PrimaryButton onClick={requestAvailability}>
                                Request Availability
                            </PrimaryButton>
                        </div>
                    ) : null}
                    {hasRequestedAvailability ? (
                        <InlineBanner type="info">
                            Availability request sent!
                        </InlineBanner>
                    ) : null}
                    {listing.availability?.lastRequestedAt ? (
                        <div className="mt-4 space-y-2">
                            <p className="text-gray-500 text-sm">
                                Last requested{" "}
                                {daysAgo(listing.availability?.lastRequestedAt)}.
                            </p>
                        </div>
                    ) : null}
                </div>
            ) : (
                <div className="space-y-2">
                    {listing.availability?.status ===
                    EvergreenCommunityListingAvailability.Available ? (
                        <InlineBanner type="success">
                            {listing.name} has a vacancy.
                        </InlineBanner>
                    ) : (
                        <InlineBanner type="error">
                            {listing.name} does not have a vacancy at this time. Check
                            back in a week for updated availability.
                        </InlineBanner>
                    )}
                    {listing.availability?.notes ? (
                        <div className="space-y-1">
                            <p className="font-medium text-gray-500">Additional Notes</p>
                            <p className="text-gray-600">{listing.availability.notes}</p>
                        </div>
                    ) : null}
                    <div className="mt-4">
                        <p className="text-gray-500 text-sm">
                            Last updated {daysAgo(listing.availability?.lastUpdatedAt)}.
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

const CommunityPricing = (props: Props) => {
    const { listing } = props;

    const formatPriceRange = () => {
        if (!listing.pricing) return null;
        const { minPrice, maxPrice } = listing.pricing;
        if (minPrice && maxPrice) {
            return `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)} per month`;
        }
        if (minPrice) {
            return `Starting at ${formatCurrency(minPrice)} per month`;
        }
        if (maxPrice) {
            return `Up to ${formatCurrency(maxPrice)} per month`;
        }
        return null;
    };

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                    <div className="w-7">
                        <Icon name="17_Budget" />
                    </div>
                    <span className="text-lg text-gray-700">Pricing</span>
                </div>
            </div>
            {!listing.pricing ? (
                <div className="space-y-4 mt-2">
                    <div className="text-sm text-gray-500">
                        {listing.name} has not yet shared pricing information with
                        Community Insights.
                    </div>
                </div>
            ) : (
                <div className="space-y-4 mt-2">
                    {formatPriceRange() ? (
                        <div className="space-y-1">
                            <p className="font-medium text-gray-500">Price Range</p>
                            <p className="text-gray-600">{formatPriceRange()}</p>
                        </div>
                    ) : null}
                    {listing.pricing.notes ? (
                        <div className="space-y-1">
                            <p className="font-medium text-gray-500">Notes</p>
                            <p className="text-gray-600">{listing.pricing.notes}</p>
                        </div>
                    ) : null}
                    {listing.pricing?.files?.length > 0 ? (
                        <div className="space-y-1">
                            <p className="font-medium text-gray-500">Files</p>
                            <ul className="space-y-2">
                                {listing.pricing.files.map(file => (
                                    <li key={file.id}>
                                        <a
                                            href={file.downloadUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`${defaultLinkStyle} text-sm flex items-center space-x-1`}>
                                            <ArrowDownTrayIcon className="w-4 h-4 flex-shrink-0" />
                                            <span>{file.name}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    <div>
                        <p className="text-gray-500 text-sm">
                            Last updated {daysAgo(listing.pricing.lastUpdatedAt)}.
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

const CommunityDataDisclaimer = () => {
    return (
        <div className="text-sm text-gray-500">
            Pricing and availability are provided by community operators and may change.
            Please verify directly with the community in your selection process.
        </div>
    );
};

const NoListing = () => {
    const productName = useProductName();

    return (
        <ContentSectionColumn>
            <ContentFull>
                This community was created by your agency and is not yet linked to{" "}
                {productName} Community Insights.
            </ContentFull>
        </ContentSectionColumn>
    );
};

interface CommunityInsightsTabProps {
    communityId: CommunityId;
}

export const CommunityInsightsTab: React.FC<CommunityInsightsTabProps> = props => {
    const { communityId } = props;
    const findOrCreateCommunityInsights = useFindOrCreateCommunityInsightsMutation();

    const availabilityEnabled = useFeature("communityAvailability");

    useEffect(() => {
        findOrCreateCommunityInsights.mutateAsync({ communityId });
    }, [communityId]); // eslint-disable-line react-hooks/exhaustive-deps

    const listing = findOrCreateCommunityInsights.data?.findOrCreateCommunityInsights;

    if (findOrCreateCommunityInsights.isLoading || !findOrCreateCommunityInsights.data) {
        return (
            <ContentSection2Up>
                <ContentSectionColumn>
                    <ContentFull>
                        <div className="flex items-center justify-center">
                            <Spinner />
                        </div>
                    </ContentFull>
                </ContentSectionColumn>
            </ContentSection2Up>
        );
    }

    return (
        <NgGrid>
            {listing ? (
                <>
                    <NgGrid.Column>
                        <ContentFull>
                            <CommunityDetails listing={listing} />
                        </ContentFull>
                        {availabilityEnabled ? (
                            <>
                                <ContentFull>
                                    <CommunityAvailability
                                        listing={listing}
                                        communityId={communityId}
                                    />
                                </ContentFull>{" "}
                                <ContentFull>
                                    <CommunityPricing
                                        listing={listing}
                                        communityId={communityId}
                                    />
                                </ContentFull>
                                <ContentFull>
                                    <CommunityDataDisclaimer />
                                </ContentFull>
                            </>
                        ) : null}
                    </NgGrid.Column>
                    <NgGrid.Column>
                        <ContentFull>
                            <CommunityInsightsReviews
                                communityId={communityId}
                                showHeader={true}
                            />
                        </ContentFull>
                    </NgGrid.Column>
                </>
            ) : (
                <NoListing />
            )}
        </NgGrid>
    );
};
